/**
 * FamilyGate — family-first routing guard.
 *
 * Rendered as a pathless layout route INSIDE the auth-protected tree. It
 * hydrates the family store for the signed-in user and enforces the
 * family-first invariant: users without any family membership are sent to
 * `/onboarding` (create or join a family) before any family-scoped feature
 * becomes reachable.
 *
 * Hydration FAILURES (backend unreachable / unprovisioned) render an explicit
 * error + retry screen instead — funneling them to onboarding would invite
 * users to create families that cannot persist.
 *
 * @module features/family/components/FamilyGate
 */

import { AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { LoadingScreen } from '@/shared/components/feedback/LoadingScreen';
import { Button } from '@/shared/components/ui/button';
import { RouteKey, ROUTE_PATHS } from '@/shared/constants/routes';
import { useFamilyStore } from '../stores/familyStore';

export function FamilyGate() {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const hydrated = useFamilyStore((state) => state.hydrated);
  const loading = useFamilyStore((state) => state.loading);
  const hasFamilies = useFamilyStore((state) => state.families.length > 0);
  const error = useFamilyStore((state) => state.error);
  const hydrate = useFamilyStore((state) => state.hydrate);

  useEffect(() => {
    if (userId !== null && !hydrated && !loading) {
      void hydrate();
    }
  }, [userId, hydrated, loading, hydrate]);

  if (!hydrated) {
    return <LoadingScreen />;
  }

  if (!hasFamilies) {
    // Retry in flight: hydrate() clears `error` while `hydrated` stays true —
    // without this branch the gate would flash the onboarding redirect.
    if (loading) {
      return <LoadingScreen />;
    }

    if (error !== null) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div
            role="alert"
            className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-center"
          >
            <AlertTriangle
              className="mx-auto size-8 text-destructive"
              aria-hidden="true"
            />
            <h1 className="text-lg font-semibold text-card-foreground">
              Семействата не можаха да бъдат заредени
            </h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button
              type="button"
              onClick={() => void hydrate()}
              disabled={loading}
            >
              Опитай отново
            </Button>
          </div>
        </div>
      );
    }

    return <Navigate to={ROUTE_PATHS[RouteKey.ONBOARDING]} replace />;
  }

  return <Outlet />;
}
